import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import moment from "moment-timezone";
import { ALERT, USER } from "@constants";
import { updateUser } from "@actions/user";
import {
  getCanonicalTimezone,
  getTradingViewTimezone,
  getUserTimezone,
  upsertTradingViewTimezone,
} from "@helpers/timezone";

/**
 * @param {string|null} profileTimezone zone of the user in Redux
 * @returns {string} zone saved in this browser, else the profile's, else the browser's
 */
const readProfileTimezone = (profileTimezone) =>
  getCanonicalTimezone(getTradingViewTimezone() || profileTimezone || getUserTimezone());

/**
 * Time zone of the profile (E24): the chart has no zone of its own. Saving
 * goes the way of the profile page: POST /users/update through updateUser,
 * the user in Redux and the zone the profile page reads first
 * (upsertTradingViewTimezone). The zone is read in the profile page's order
 * (useProfileUserData): the one saved in this browser first; a zone saved on
 * the server is copied there when the user loads (syncSavedTimezone).
 * The zone is kept in state and read again after saving: updateUser puts the
 * new zone into Redux before it is written to localStorage, so the re-render
 * from Redux still reads the old zone from the browser, and the second
 * USER_FIELD with the same value brings no re-render.
 *
 * @returns {{
 *   timezone: string,
 *   saveTimezone: (timezone: string) => Promise<void>,
 *   isSaving: boolean,
 * }} timezone: IANA zone of the profile, the browser's one until the profile has it
 */
const useProfileTimezone = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const profileTimezone = useSelector((state) => state.user.user?.timezone || null);
  const [isSaving, setIsSaving] = useState(false);
  const [timezone, setTimezone] = useState(() => readProfileTimezone(profileTimezone));

  // The zone of the profile also rules the dates outside the chart: History and
  // wallet tables read "userTimezone", the rest of the app uses the moment default
  useEffect(() => {
    if (!profileTimezone) return;

    try {
      moment.tz.setDefault(profileTimezone);
      localStorage.setItem("userTimezone", profileTimezone);
    } catch (error) {
      // The storage may be unavailable, the chart keeps working without it
    }
  }, [profileTimezone]);

  // The zone of the user may change outside the chart (profile page, login)
  useEffect(() => {
    setTimezone(readProfileTimezone(profileTimezone));
  }, [profileTimezone]);

  const saveTimezone = useCallback(
    async (nextTimezone) => {
      if (!nextTimezone || nextTimezone === timezone) return;

      setIsSaving(true);

      try {
        const response = await dispatch(updateUser({ data: { timezone: nextTimezone } }));
        const savedTimezone = response?.data?.user?.timezone ?? nextTimezone;

        // As the profile page does: the answer may come without the user
        dispatch({ type: USER.USER_FIELD, payload: { timezone: savedTimezone } });
        upsertTradingViewTimezone(savedTimezone);
        setTimezone(readProfileTimezone(savedTimezone));
      } catch (error) {
        // updateUser shows the message of the server when there is one
        if (!error.response?.data?.message) {
          dispatch({
            type: ALERT.ALERT_ERROR,
            payload: intl.formatMessage({ id: "common.failed" }),
          });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [dispatch, intl, timezone],
  );

  return { timezone, saveTimezone, isSaving };
};

export default useProfileTimezone;
